import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, FileSpreadsheet } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { YearSummaryCards } from '../components/reports/YearSummaryCards';
import { MonthlyBarChart } from '../components/reports/MonthlyBarChart';
import { MonthlyTable } from '../components/reports/MonthlyTable';

const currentRoYear = () => Number(new Intl.DateTimeFormat('en', { year: 'numeric', timeZone: 'Europe/Bucharest' }).format(new Date()));

export const RapoarteV2 = () => {
    const [year, setYear] = useState(currentRoYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exportingInventory, setExportingInventory] = useState(false);

    const loadReport = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/v2/reports/annual?year=${year}`);
            setData(res.data);
        } catch (err) {
            toast.error('Eroare la încărcarea rapoartelor');
        }
        setLoading(false);
    }, [year]);

    useEffect(() => { loadReport(); }, [loadReport]);

    const exportInventoryReport = async () => {
        setExportingInventory(true);
        try {
            const response = await api.get('/inventory/report/excel', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `raport_inventar_${new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Bucharest' })}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Raport inventar descărcat!');
        } catch (err) {
            toast.error(err.response?.status === 404 ? 'Nu există produse vândute pentru raport' : 'Eroare la generare raport');
        }
        setExportingInventory(false);
    };

    const years = data?.available_years?.length ? data.available_years : [year];

    return (
        <div className="pb-24" data-testid="rapoarte-v2-page">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Rapoarte</h1>
                    <p className="text-zinc-500">Vânzări, cheltuieli și profit pe an</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                        <SelectTrigger data-testid="report-year-select" className="w-28 font-semibold">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {years.map(y => (
                                <SelectItem key={y} value={String(y)} data-testid={`report-year-option-${y}`}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button data-testid="export-inventory-btn" onClick={exportInventoryReport} disabled={exportingInventory} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        <FileSpreadsheet size={18} className="mr-2" />
                        {exportingInventory ? 'Generare...' : 'Raport Inventar'}
                    </Button>
                    <button data-testid="reload-reports-btn" onClick={loadReport} className="p-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition-colors" title="Reîncarcă">
                        <RefreshCw size={20} className={`text-zinc-600 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {loading && !data ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
                    </div>
                    <Skeleton className="h-72 rounded-xl" />
                    <Skeleton className="h-64 rounded-xl" />
                </div>
            ) : data ? (
                <>
                    <YearSummaryCards totals={data.totals} year={data.year} />
                    <MonthlyBarChart months={data.months} year={data.year} />
                    <MonthlyTable months={data.months} totals={data.totals} year={data.year} />
                </>
            ) : (
                <div className="text-center py-8 text-zinc-500">Nu s-au putut încărca datele</div>
            )}
        </div>
    );
};

export default RapoarteV2;
